import { Component, EventEmitter, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ContentUtil } from '../../../../utils/content/content.util';
import {
    catchError,
    combineLatest,
    map,
    Observable,
    Subscription,
    switchMap,
    take,
    tap,
} from 'rxjs';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { CONSTANTS } from '../../../../../common/constants/constants';
import { PinValidationService } from '../../../../services/pin-validation/pin-validation.service';
import { PERMISSION_REQUEST_ERRORS } from '../../../../enums/permission_request_error.enum';
import { ActivatedRoute } from '@angular/router';
import { TOKEN_PARAM } from '../../../../constants/application-routes';
import { ErrorService } from '../../../../services/generic-error/error.service';
import {
    ErrorPage,
    errorPageMap,
    granteeIneligibleReasonPageMap,
} from '../../../../services/generic-error/error-page-utils';
import { AccessPermissionsService } from '../../../../services/access-permissions/access-permissions.service';
import { Store } from '@ngrx/store';
import {
    selectGranteeAccessType,
    selectGranteeClientType,
    selectIsMultiAccount,
} from '../../../../store/selectors/grantee.selector';
import { setBulkAcceptResponse } from '../../../../store/actions/grantee-flow.actions';
import {
    AcceptPermissionResponseDTO,
    BulkAcceptPermissionResponseDTO,
} from '../../../../../common/dtos/access-permissions.dto';
import { AccessTypeSelection } from '../../../../../common/enums/access-type-selection.enum';
import { ClientTypes } from '../../../../../common/enums/client-types.enum';
import { AdobeTagging } from '../../../../utils/decorators/dev/adobe-launch';
import { configData } from './grantee-pin-input-adobe';
import { HintErrorModule } from '@vg-constellation/angular-20/hint-error';
import { LoggerService } from '../../../../services/logger/logger.service';
import { selectUserInfo } from '../../../../store/selectors/account-permissions.selector';

@AdobeTagging(configData)
@Component({
    selector: 'zci-grantee-pin-input',
    templateUrl: 'grantee-pin-input.component.html',
    styleUrls: ['grantee-pin-input.component.scss'],
    imports: [ReactiveFormsModule, HintErrorModule],
})
export class GranteePinInputComponent implements OnInit, OnDestroy {
    readonly accessTypeSelection = AccessTypeSelection;
    @Input() nextStepClicked: EventEmitter<any> = new EventEmitter<any>();
    @Input() nextStepReady: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() isSubmitStep: boolean;
    granteePinContent = ContentUtil.granteeFragmentsContent.frmcGranteeFlow.frmcUniquePin;
    pinFormControlName: string = 'pin';
    pinForm = new FormGroup({
        [this.pinFormControlName]: new FormControl(null, [
            Validators.pattern(CONSTANTS.UNIQUE_PIN_VALIDATION_PATTERN),
            Validators.required,
        ]),
    });
    pinValue: string;
    tokenValue: string;
    readonly subs: Subscription = new Subscription();
    private pinValidationService = inject(PinValidationService);
    private route = inject(ActivatedRoute);
    private errorService = inject(ErrorService);
    private accessPermissionsService = inject(AccessPermissionsService);
    private store = inject(Store);
    private logger = inject(LoggerService);
    granteeAccessType$: Observable<AccessTypeSelection> =
        this.store.select(selectGranteeAccessType);

    granteeClientType$: Observable<ClientTypes> = this.store.select(selectGranteeClientType);
    isMultiAccount$: Observable<boolean> = this.store.select(selectIsMultiAccount);
    private readonly adobePageLoadFnMap = {
        [ClientTypes.NON_VGCLIENT]: {
            [AccessTypeSelection.INFORMATION_ONLY]: this.adobeNVCInfoOnlyPageLoad,
            [AccessTypeSelection.LIMITED_ACCESS]: this.adobeNVCLimitedOnlyPageLoad,
        },
        [ClientTypes.UNKNOWN]: {
            [AccessTypeSelection.INFORMATION_ONLY]: this.adobeInfoOnlyPageLoad,
            [AccessTypeSelection.LIMITED_ACCESS]: this.adobeLimitedOnlyPageLoad,
        },
    };
    private readonly adobeNextStepLoadFnMap = {
        [ClientTypes.NON_VGCLIENT]: {
            [AccessTypeSelection.INFORMATION_ONLY]: this.adobeNVCNextStepLoadInfoOnly,
            [AccessTypeSelection.LIMITED_ACCESS]: this.adobeNVCNextStepLoadLimited,
        },
        [ClientTypes.UNKNOWN]: {
            [AccessTypeSelection.INFORMATION_ONLY]: this.adobeNextStepLoadInfoOnly,
            [AccessTypeSelection.LIMITED_ACCESS]: this.adobeNextStepLoadLimited,
        },
    };

    get pinFormControl(): AbstractControl {
        return this.pinForm.get(this.pinFormControlName);
    }

    get getPinErrorMessage(): string | undefined {
        if (this.pinFormControl.hasError('required')) {
            return this.granteePinContent.frmfUniquePin.errEnterPin;
        }

        if (this.pinFormControl.hasError('pattern')) {
            return this.granteePinContent.frmfUniquePin.errEnterValidPin;
        }
        return undefined;
    }

    ngOnInit(): void {
        this.subs.add(this.nextStepClicked.subscribe(this.onNextStepLoad.bind(this)));
        this.route.paramMap
            .pipe(
                map((params) => {
                    this.tokenValue = params.get(TOKEN_PARAM);
                }),
            )
            .subscribe();

        this.subs.add(
            combineLatest([this.granteeAccessType$, this.granteeClientType$]).subscribe(
                ([accessType, clientType]) => this.adobePageLoadFnMap[clientType][accessType](),
            ),
        );
        // TODO: Need to add some configuration based on client type
        this.subs.add(
            this.granteeClientType$.subscribe((clientType) => {
                console.log(`clientType: ${clientType}`); // This is just check the data availabilty in this page, We can remove once we have the configuration
            }),
        );
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    private adobeInfoOnlyPageLoad(): void {}

    private adobeLimitedOnlyPageLoad(): void {}

    private adobeNextStepLoadInfoOnly(): void {}

    private adobeNextStepLoadLimited(): void {}

    private adobeNVCInfoOnlyPageLoad(): void {}

    private adobeNVCLimitedOnlyPageLoad(): void {}

    private adobeNVCNextStepLoadInfoOnly(): void {}

    private adobeNVCNextStepLoadLimited(): void {}

    private handleNextStepLoadAdobe(): void {
        combineLatest([this.granteeAccessType$, this.granteeClientType$]).subscribe(
            ([accessType, clientType]) => this.adobeNextStepLoadFnMap[clientType][accessType](),
        );
    }

    private onNextStepLoad(): void {
        this.pinValue = this.pinForm.get(this.pinFormControlName).value;
        this.pinForm.markAllAsTouched();

        if (!this.pinForm.valid) {
            this.nextStepReady.emit(false);
        } else {
            this.pinValidationService
                .validatePin(this.pinValue, this.tokenValue)
                .subscribe((response) => {
                    if (response.error === PERMISSION_REQUEST_ERRORS.PIN_IS_INCORRECT) {
                        this.pinFormControl.setErrors({ pattern: true });
                        this.nextStepReady.emit(false);
                        return;
                    }

                    if (Object.hasOwn(errorPageMap, response.error)) {
                        this.errorService.showError(errorPageMap[response.error]);
                        this.nextStepReady.emit(false);
                        return;
                    }

                    if (
                        Object.hasOwn(
                            granteeIneligibleReasonPageMap,
                            response.granteeIneligibleReason,
                        )
                    ) {
                        this.errorService.showError(
                            granteeIneligibleReasonPageMap[response.granteeIneligibleReason],
                        );
                        this.nextStepReady.emit(false);
                        return;
                    }
                    if (this.isSubmitStep) {
                        combineLatest([
                            this.isMultiAccount$,
                            this.store.select(selectUserInfo),
                        ])
                            .pipe(
                                take(1),
                                switchMap(([isMultiAccount, granteeInfo]) => {
                                    return (isMultiAccount
                                        ? this.accessPermissionsService.bulkAcceptPermission(
                                              this.tokenValue,
                                          )
                                        : this.accessPermissionsService.acceptPermission(
                                              this.tokenValue,
                                          )
                                    ).pipe(
                                        tap(
                                            (
                                                response:
                                                    | AcceptPermissionResponseDTO
                                                    | BulkAcceptPermissionResponseDTO,
                                            ) => {
                                                if ('results' in response && response.results) {
                                                    const allAccountsFailed = response.results.every(
                                                        (r) => !r.confirmationNumber,
                                                    );
                                                    const hasPartialFailure = response.results.some(
                                                        (r) => !r.confirmationNumber,
                                                    );

                                                    if (allAccountsFailed) {
                                                        this.logger.info(
                                                            `[GranteePinInput] Navigating to ACCEPT_ACCESS_FLOW_SUBMIT_ALL_FAILED. ` +
                                                            `Grantee POID: ${granteeInfo?.clientPoid}, ` +
                                                            `Total results: ${response.results?.length ?? 0}, Successful: 0, Failed: ${response.results?.length ?? 0}` +
                                                            `Access level: INFO_ONLY`,
                                                        );
                                                        this.errorService.showError(
                                                            ErrorPage.ACCEPT_ACCESS_FLOW_SUBMIT_ALL_FAILED,
                                                        );
                                                        this.nextStepReady.emit(false);
                                                        return;
                                                    }

                                                    if (hasPartialFailure) {
                                                        const failedCount = response.results.filter((r) => !r.confirmationNumber).length;
                                                        const successCount = response.results.length - failedCount;
                                                        this.logger.info(
                                                            `[GranteePinInput] Navigating to ACCEPT_ACCESS_FLOW_PARTIAL_SUCCESS. ` +
                                                            `Grantee POID: ${granteeInfo?.clientPoid}, ` +
                                                            `Total results: ${response.results?.length ?? 0}, Successful: ${successCount}, Failed: ${failedCount}` +
                                                            `Access level: INFO_ONLY`,
                                                        );
                                                        this.store.dispatch(
                                                            setBulkAcceptResponse({ response }),
                                                        );
                                                        this.nextStepReady.emit(false);
                                                        this.errorService.showError(
                                                            ErrorPage.ACCEPT_ACCESS_FLOW_PARTIAL_SUCCESS,
                                                        );
                                                        return;
                                                    }
                                                }

                                                this.nextStepReady.emit(!response.error);

                                                if (response.error) {
                                                    this.logger.info(
                                                        `[GranteePinInput] Error response received. ` +
                                                        `Grantee POID: ${granteeInfo?.clientPoid}, Error: ${response.error}` +
                                                        `Access level: INFO_ONLY`,
                                                    );
                                                    this.errorService.showError(
                                                        errorPageMap[response.error],
                                                    );
                                                } else {
                                                    this.logger.info(
                                                        `[GranteePinInput] Navigating to AAF_ALL_SUCCESS page. ` +
                                                        `Grantee POID: ${granteeInfo?.clientPoid}` +
                                                        `Access level: INFO_ONLY`,
                                                    );
                                                    this.handleNextStepLoadAdobe();
                                                }
                                            },
                                        ),
                                        catchError(() => {
                                            this.logger.info('[GranteePinInput] Technical error occurred during accept.');
                                            this.errorService.showError(ErrorPage.TECH_DIFF);
                                            return Promise.reject();
                                        }),
                                    );
                                }),
                            )
                            .subscribe();
                    } else {
                        this.handleNextStepLoadAdobe();
                        this.nextStepReady.emit(true);
                    }
                });
        }
    }
}
